import React, { useState } from 'react';
import { FiFileText, FiPlus, FiSearch, FiX, FiList } from 'react-icons/fi';
import AccountingSidebar from '../components/AccountingSidebar';
import GenericAddModal from '../components/GenericAddModal';
import useLocalState from '../hooks/useLocalState';
import { sortData } from '../utils/tableUtils';

export default function GenericAccountingPage({ title }) {
  // Use dynamic cache key based on the page title
  const cacheKey = `generic_acc_${title.replace(/\s+/g, '_').toLowerCase()}_v2`;
  const [data, setData] = useLocalState(cacheKey, []);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'ref', direction: 'asc' });

  const handleAdd = (newData) => {
    setData([newData, ...data]);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const sortedData = sortData(data, sortConfig);

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <span className="text-[10px] text-gray-400 opacity-0 group-hover:opacity-100 ml-1">▲</span>;
    return <span className="text-[10px] text-black ml-1">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>;
  };

  return (
    <div className="flex h-full font-sans overflow-hidden bg-white">
      <AccountingSidebar />
      <div className="flex-1 flex flex-col bg-[#a6e4e7] p-2">
        <div className="flex justify-between items-center mb-3 px-1 text-gray-800">
          <div className="flex items-center space-x-2">
            <FiFileText className="text-xl opacity-70 text-teal-800" />
            <span className="font-medium text-[15px] text-teal-700">{title} ({data.length})</span>
          </div>
          <div className="flex items-center space-x-1.5">
            <select className="border border-gray-400 bg-white py-1 px-2 text-sm rounded-sm outline-none w-16 shadow-sm">
              <option>20</option>
            </select>
            <button onClick={() => setIsModalOpen(true)} className="bg-[#0000a0] text-white p-1.5 rounded shadow-sm hover:bg-blue-900 transition-colors">
              <FiPlus className="text-sm" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-x-auto bg-white border border-black shadow-sm">
          <table className="min-w-full border-collapse text-[12px] text-gray-800">
            <thead>
              <tr className="bg-[#a6e4e7]">
                <td className="border border-black p-1.5 w-1/4">
                  <input type="text" className="w-full border border-gray-300 p-1 bg-white outline-none h-7 shadow-inner" />
                </td>
                <td className="border border-black p-1.5 w-1/2">
                  <input type="text" className="w-full border border-gray-300 p-1 bg-white outline-none h-7 shadow-inner" />
                </td>
                <td className="border border-black p-1.5 text-center w-24">
                  <div className="flex justify-center space-x-2 text-lg font-bold">
                    <FiSearch className="text-black cursor-pointer hover:opacity-70" />
                    <FiX className="text-black cursor-pointer hover:opacity-70" />
                  </div>
                </td>
              </tr>
              <tr className="bg-[#a6e4e7] text-teal-900 select-none">
                <th onClick={() => handleSort('ref')} className="font-normal p-2 border border-black text-left text-teal-700 cursor-pointer hover:bg-teal-200 group">
                  Reference <SortIcon column="ref" />
                </th>
                <th onClick={() => handleSort('desc')} className="font-normal p-2 border border-black text-left text-teal-700 cursor-pointer hover:bg-teal-200 group">
                  Description <SortIcon column="desc" />
                </th>
                <th className="font-normal p-2 border border-black text-center text-teal-700">
                  <FiList className="inline text-black" />
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.length === 0 ? (
                <tr>
                  <td colSpan="3" className="p-12 text-center text-gray-400 italic bg-gray-50 border-b border-gray-200">
                    <FiFileText className="text-4xl mx-auto mb-3 opacity-30" />
                    No records found for {title}. Click the + button to add one.
                  </td>
                </tr>
              ) : (
                sortedData.map((row, idx) => (
                  <tr key={idx} className="bg-white hover:bg-blue-50 transition-colors">
                    <td className="p-2 border border-black text-gray-800 font-medium">{row.ref}</td>
                    <td className="p-2 border border-black text-gray-800">{row.desc}</td>
                    <td className="p-2 border border-black text-center"></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <GenericAddModal 
          isOpen={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
          onAdd={handleAdd} 
          title={`Add ${title} Record`} 
          fields={[
            { name: 'ref', label: 'Reference' },
            { name: 'desc', label: 'Description' }
          ]} 
        />
      </div>
    </div>
  );
}